import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import type { MeasureEntityRef } from '../types/measure-types';
import {
    MeasureDistancePrimitiveKind,
    type MeasureClosestPointResult,
    type MeasureDistanceGeometry,
    type MeasureNormalDistanceResult,
} from '../types/measure-distance-types';
import { resolveNormalDistanceBetweenGeometries } from '../utils/measureNormalDistance';
import { MeasureEntityGeometryAdapter } from './MeasureEntityGeometryAdapter';

/**
 * 垂直距离求解器。
 *
 * 职责：在双选测量中，当一方为点、另一方为直线/平面时，计算 SolidWorks 语义的垂直距离，
 * 并判定是否应与全局最短距离分开展示。
 */
export class MeasureNormalDistanceResolver {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 几何适配器。 */
    private readonly geometryAdapter: MeasureEntityGeometryAdapter;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.geometryAdapter = new MeasureEntityGeometryAdapter(viewer);
    }

    /**
     * 求解两测量实体之间的垂直距离。
     *
     * @param firstEntity 先选实体。
     * @param secondEntity 后选实体。
     * @param closest 已求得的全局最近点结果。
     * @returns 垂直距离结果；不支持或无需展示时返回 null。
     */
    public resolve(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        closest: MeasureClosestPointResult,
    ): MeasureNormalDistanceResult | null {
        const geometryA = this.geometryAdapter.adapt(firstEntity);
        const geometryB = this.geometryAdapter.adapt(secondEntity);
        if (!geometryA || !geometryB) {
            return null;
        }

        const pointTargetPair = resolvePointTargetPair(geometryA, geometryB);
        if (!pointTargetPair) {
            return null;
        }

        const boundedClosestOnTarget = pointTargetPair.pointOnFirstEntity
            ? closest.to
            : closest.from;

        const normalDistance = resolveNormalDistanceBetweenGeometries(
            pointTargetPair.point,
            pointTargetPair.target,
            closest.distance,
            boundedClosestOnTarget,
        );
        if (!normalDistance || !normalDistance.shouldRender) {
            return null;
        }

        return normalDistance;
    }
}

/**
 * 点-目标几何体配对。
 */
interface PointTargetPair {
    /** 点世界坐标。 */
    point: THREE.Vector3;
    /** 直线/平面目标几何体。 */
    target: MeasureDistanceGeometry;
    /** 点是否落在先选实体上（用于定位有限体最近点）。 */
    pointOnFirstEntity: boolean;
}

/**
 * 从两几何体中解析「点 + 直线/平面」配对。
 *
 * @param geometryA 几何体 A。
 * @param geometryB 几何体 B。
 * @returns 点-目标配对；无法配对时返回 null。
 */
function resolvePointTargetPair(
    geometryA: MeasureDistanceGeometry,
    geometryB: MeasureDistanceGeometry,
): PointTargetPair | null {
    if (geometryA.kind === MeasureDistancePrimitiveKind.Point) {
        const target = asNormalDistanceTarget(geometryB);
        return target
            ? { point: geometryA.position, target, pointOnFirstEntity: true }
            : null;
    }

    if (geometryB.kind === MeasureDistancePrimitiveKind.Point) {
        const target = asNormalDistanceTarget(geometryA);
        return target
            ? { point: geometryB.position, target, pointOnFirstEntity: false }
            : null;
    }

    return null;
}

/**
 * 判断几何体是否可作为垂直距离目标（仅直线/平面类）。
 *
 * 设计说明：点+圆弧折线、点+曲面等非直线/平面组合不支持垂直距离图形。
 *
 * @param geometry 几何体。
 * @returns 可作为目标时返回原几何体，否则返回 null。
 */
function asNormalDistanceTarget(
    geometry: MeasureDistanceGeometry,
): MeasureDistanceGeometry | null {
    switch (geometry.kind) {
        case MeasureDistancePrimitiveKind.Segment:
        case MeasureDistancePrimitiveKind.InfiniteLine:
        case MeasureDistancePrimitiveKind.Plane:
        case MeasureDistancePrimitiveKind.MeshFace:
            return geometry;
        default:
            return null;
    }
}
